import { DmaCountry } from './dma-countries';
import { dmaCountryByAlpha2 } from './dma-country-codes';

/**
 * Spellings of countries that are neither the DMA name nor an ISO code but that
 * show up in identified sites. Ported from company-mapping's `Screening/countries.py`.
 */
const alpha2ByAlias: Record<string, string> = {
  'U.A.E.': 'AE',
  UAE: 'AE',
  Aland: 'AX',
  'St Barts': 'BL',
  'Brunei Darussalam': 'BN',
  'Bolivia (Plurinational State of)': 'BO',
  'Plurinational State of Bolivia': 'BO',
  'Caribbean Netherlands': 'BQ',
  'Congo (Kinshasa)': 'CD',
  'Congo, Democratic Republic of the': 'CD',
  'Congo-Kinshasa': 'CD',
  'DR Congo': 'CD',
  DRC: 'CD',
  'Congo (Brazzaville)': 'CG',
  'Congo-Brazzaville': 'CG',
  'Ivory Coast': 'CI',
  'Mainland China': 'CN',
  'P.R. China': 'CN',
  PRC: 'CN',
  "People's Republic of China": 'CN',
  'Cape Verde': 'CV',
  'Czech Republic': 'CZ',
  'Falkland Islands (Malvinas)': 'FK',
  'Federated States of Micronesia': 'FM',
  'Micronesia (Federated States of)': 'FM',
  Britain: 'GB',
  England: 'GB',
  'Great Britain': 'GB',
  'Northern Ireland': 'GB',
  Scotland: 'GB',
  'U.K.': 'GB',
  UK: 'GB',
  'United Kingdom of Great Britain and Northern Ireland': 'GB',
  Wales: 'GB',
  'Hong Kong SAR': 'HK',
  'Hong Kong, China': 'HK',
  'Republic of Ireland': 'IE',
  'Iran (Islamic Republic of)': 'IR',
  'Islamic Republic of Iran': 'IR',
  'Kyrgyz Republic': 'KG',
  'St Kitts and Nevis': 'KN',
  "Democratic People's Republic of Korea": 'KP',
  "Korea, Democratic People's Republic of": 'KP',
  Korea: 'KR',
  'Korea, Republic of': 'KR',
  'Republic of Korea': 'KR',
  'Lao PDR': 'LA',
  "Lao People's Democratic Republic": 'LA',
  'St Lucia': 'LC',
  'Moldova, Republic of': 'MD',
  'Republic of Moldova': 'MD',
  'Saint Martin': 'MF',
  Macedonia: 'MK',
  'Republic of North Macedonia': 'MK',
  Burma: 'MM',
  'Macao SAR': 'MO',
  Macau: 'MO',
  Holland: 'NL',
  'Kingdom of the Netherlands': 'NL',
  'Pitcairn Islands': 'PN',
  'Occupied Palestinian Territory': 'PS',
  'Palestine, State of': 'PS',
  'Palestinian Authority': 'PS',
  'Palestinian Territories': 'PS',
  'State of Palestine': 'PS',
  'Russian Federation': 'RU',
  'Saint Helena, Ascension and Tristan da Cunha': 'SH',
  Svalbard: 'SJ',
  'Slovak Republic': 'SK',
  'Sint Maarten': 'SX',
  'Syrian Arab Republic': 'SY',
  Swaziland: 'SZ',
  'East Timor': 'TL',
  Turkiye: 'TR',
  'Taiwan, Province of China': 'TW',
  'Tanzania, United Republic of': 'TZ',
  'United Republic of Tanzania': 'TZ',
  'U.S.': 'US',
  'U.S.A.': 'US',
  'United States of America': 'US',
  Vatican: 'VA',
  'Vatican City': 'VA',
  'St Vincent and the Grenadines': 'VC',
  'Bolivarian Republic of Venezuela': 'VE',
  'Venezuela (Bolivarian Republic of)': 'VE',
  BVI: 'VG',
  'Virgin Islands (British)': 'VG',
  'US Virgin Islands': 'VI',
  'Virgin Islands (U.S.)': 'VI',
  'Viet Nam': 'VN',
  'Republic of South Africa': 'ZA',
};

const countryEntries = Object.entries(dmaCountryByAlpha2);

// Only countries are keyed by name: a site sits in one country, so a region such as
// "Europe" carries no usable share and must not resolve.
const dmaCountryByNameKey = new Map(countryEntries.map(([, { dmaCountry }]) => [nameKey(dmaCountry), dmaCountry]));

const dmaCountryByCode = new Map(
  countryEntries.flatMap(([alpha2, { alpha3, dmaCountry }]) => [
    [alpha2, dmaCountry],
    [alpha3, dmaCountry],
  ]),
);

const dmaCountryByAliasKey = new Map(
  Object.entries(alpha2ByAlias).map(([alias, alpha2]) => [nameKey(alias), dmaCountryByAlpha2[alpha2].dmaCountry]),
);

// Each DMA name belongs to one code, so the name leads back to it.
const alpha3ByDmaCountry = new Map(countryEntries.map(([, { alpha3, dmaCountry }]) => [dmaCountry, alpha3]));

/**
 * Maps a free-text country — a DMA name in any casing or accenting, an ISO alpha-2
 * or alpha-3 code, or a known alias — to its `DMA_COUNTRIES` name, which is what
 * geo triggers match on. Anything else, including regions and ambiguous names such
 * as "Congo", is undefined: a guessed country would fire another country's triggers.
 */
export function normaliseDmaCountry(value: string | null): DmaCountry | undefined {
  if (value === null) return undefined;

  const key = nameKey(value);
  return (
    dmaCountryByNameKey.get(key) ?? dmaCountryByCode.get(value.trim().toUpperCase()) ?? dmaCountryByAliasKey.get(key)
  );
}

/**
 * The ISO alpha-3 code of the country `normaliseDmaCountry` resolves a free-text country to, for
 * callers that place a site by its code rather than match triggers on its name: the Supplier base
 * page's regions (PLT-7495). Undefined where it resolves none.
 */
export function normaliseCountryAlpha3(value: string | null): string | undefined {
  const country = normaliseDmaCountry(value);
  return country === undefined ? undefined : alpha3ByDmaCountry.get(country);
}

/** Folds case, accents, punctuation and a leading "the" so spelling variants share a key. */
function nameKey(value: string): string {
  return value
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim()
    .replace(/^the /, '');
}
